/**
 * Müstakil Dükkan - Product Variant Controller
 * 
 * Handles:
 * - Color/Pack variant selection
 * - Image gallery filtering by alt tags
 * - Dynamic title updates
 * - Color fallback when variant unavailable
 * - Auto pack-switch when color's current pack is unavailable
 */

class ProductVariantController {
  constructor(productData, variantsData) {
    this.product = productData;
    this.variants = variantsData;
    this.imagesData = this.parseImagesData();
    this.currentVariant = this.getInitialVariant();
    
    this.init();
  }
  
  init() {
    this.bindEvents();
    this.applyMixedSwatches();
    this.updateUI();
  }

  applyMixedSwatches() {
    const solidHexMap = {
      'Gonca Pembe': '#c79383',
      'İnci Beyaz': '#d4ddde', 'Inci Beyaz': '#d4ddde',
      'Sayfi Mavi': '#537da8',
      'Müdavim Yeşil': '#9e9b76', 'Mudavim Yesil': '#9e9b76',
      'Dem Kırmızı': '#9c3b2a', 'Dem Kirmizi': '#9c3b2a',
      'Firuze Petrol Yeşili': '#013d4e', 'Firuze Petrol Yesili': '#013d4e'
    };
    const solidColors = [...new Set(this.variants.map(v => v.option1))].filter(c => solidHexMap[c]);

    document.querySelectorAll('.color-swatch').forEach(swatch => {
      const color = swatch.dataset.colorValue;
      if (solidHexMap[color]) return; // solid color, skip

      const inner = swatch.querySelector('.color-swatch-inner');
      if (!inner) return;

      let hexes;
      if (color.includes(' + ')) {
        hexes = color.split(' + ').map(c => solidHexMap[c.trim()] || '#cccccc');
      } else {
        // "Karışık" type — all solid colors
        hexes = solidColors.map(c => solidHexMap[c]);
      }

      if (hexes.length < 2) return;

      const step = 100 / hexes.length;
      const stops = hexes.map((hex, i) => {
        const start = Math.round(step * i);
        const end = i === hexes.length - 1 ? 100 : Math.round(step * (i + 1));
        return `${hex} ${start}% ${end}%`;
      });
      inner.style.background = `conic-gradient(${stops.join(', ')})`;

      // Move mixed swatches to the front of the grid
      const grid = swatch.parentElement;
      if (grid) grid.prepend(swatch);
    });
  }
  
  parseImagesData() {
    const dataEl = document.getElementById('product-images-data');
    if (!dataEl) return { images: [] };
    try {
      return JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('Failed to parse images data:', e);
      return { images: [] };
    }
  }
  
  getInitialVariant() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    
    if (variantId) {
      const variant = this.variants.find(v => v.id == variantId);
      if (variant) return variant;
    }
    
    return this.variants.find(v => v.available) || this.variants[0];
  }
  
  bindEvents() {
    document.querySelectorAll('.color-swatch').forEach(swatch => {
      swatch.addEventListener('click', (e) => {
        if (e.currentTarget.disabled) return;
        const color = e.currentTarget.dataset.colorValue;
        this.selectColor(color);
      });
    });
    
    document.querySelectorAll('.pack-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        if (e.currentTarget.disabled) return;
        const pack = e.currentTarget.dataset.packValue;
        this.selectPack(pack);
      });
    });
    
    document.querySelectorAll('.gallery-thumbnail').forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        const imageId = e.currentTarget.dataset.imageId;
        this.selectImage(imageId);
      });
    });
    
    const prevBtn = document.querySelector('.gallery-nav-prev');
    const nextBtn = document.querySelector('.gallery-nav-next');
    
    if (prevBtn) prevBtn.addEventListener('click', () => this.navigateGallery(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => this.navigateGallery(1));

    // Touch swipe for mobile
    const galleryMain = document.querySelector('.gallery-main');
    if (galleryMain) {
      let touchStartX = 0;
      let touchEndX = 0;
      galleryMain.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });
      galleryMain.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        const diff = touchStartX - touchEndX;
        if (Math.abs(diff) > 40) {
          this.navigateGallery(diff > 0 ? 1 : -1);
        }
      }, { passive: true });
    }
  }
  
  selectColor(color) {
    const currentPack = this.currentVariant.option2;

    // First try: same color + current pack
    let targetVariant = this.findVariant(color, currentPack);

    // If not available, find first available pack for this color
    if (!targetVariant || !targetVariant.available) {
      targetVariant = this.findFirstAvailableVariantForColor(color);
    }

    if (targetVariant) {
      this.currentVariant = targetVariant;
      this.updateUI();
      this.updateURL();
      this.publishVariantView();
    }
  }

  selectPack(pack) {
    const currentColor = this.currentVariant.option1;

    // First try: current color + new pack
    let targetVariant = this.findVariant(currentColor, pack);

    // If not available, find first available color for this pack
    if (!targetVariant || !targetVariant.available) {
      targetVariant = this.findFirstAvailableVariantForPack(pack);
    }

    if (targetVariant) {
      this.currentVariant = targetVariant;
      this.updateUI();
      this.updateURL();
      this.publishVariantView();
    }
  }

  publishVariantView() {
    if (typeof fbq === 'undefined') return;

    const variantId = String(this.currentVariant.id);
    const variantTitle = this.currentVariant.title || '';
    const parts = variantTitle.split(' / ');
    const color = parts[0] || '';
    const pack = parts[1] || '';

    let contentName = this.product.title;
    if (color) {
      if (!pack || pack === 'Tekli') {
        contentName = this.product.title + ' - ' + color;
      } else {
        contentName = this.product.title + ' - ' + color + ' ' + pack;
      }
    }

    fbq('track', 'ViewContent', {
      content_ids: [variantId],
      content_type: 'product',
      content_name: contentName,
      content_category: this.product.type || '',
      currency: 'TRY',
      value: this.currentVariant.price / 100,
      contents: [{
        id: variantId,
        quantity: 1,
        item_price: this.currentVariant.price / 100
      }]
    });
  }
  
  findVariant(color, pack) {
    return this.variants.find(v => v.option1 === color && v.option2 === pack);
  }
  
  findFirstAvailableVariantForColor(color) {
    return this.variants.find(v => v.option1 === color && v.available);
  }
  
  findFirstAvailableVariantForPack(pack) {
    return this.variants.find(v => v.option2 === pack && v.available);
  }
  
  isColorAvailable(color) {
    return this.variants.some(v => v.option1 === color && v.available);
  }
  
  isPackAvailable(pack) {
    return this.variants.some(v => v.option2 === pack && v.available);
  }
  
  getBasePack() {
    if (!this._basePack) {
      const packs = [...new Set(this.variants.map(v => v.option2).filter(Boolean))];
      this._basePack = packs[0] || null;
    }
    return this._basePack;
  }

  isColorPackAvailable(color, pack) {
    const variant = this.findVariant(color, pack);
    return variant && variant.available;
  }
  
  updateUI() {
  this.updateTitle();
  this.updateColorSwatches();
  this.updatePackButtons();
  this.updateGallery();
  this.updatePrice();
  this.updateAddToCart();
  this.updateQuantityLimits();
  
  // Dispatch event for quantity selector
  window.dispatchEvent(new CustomEvent('variantChange', { 
    detail: { variant: this.currentVariant } 
  }));
}

updateQuantityLimits() {
  const quantityInput = document.querySelector('.md-qty-input');
  if (quantityInput) {
    const maxQty = this.currentVariant.inventory_policy === 'continue' 
      ? 9999 
      : Math.max(1, this.currentVariant.inventory_quantity || 1);
    
    quantityInput.setAttribute('max', maxQty);
    
    // Reset to 1 if current value exceeds new max
    const currentValue = parseInt(quantityInput.value) || 1;
    if (currentValue > maxQty) {
      quantityInput.value = maxQty;
    }
  }
}
  
  updateTitle() {
    const titleEl = document.getElementById('product-title-dynamic');
    const subtitleEl = document.getElementById('product-color-subtitle');
    
    if (titleEl) {
      const pack = this.currentVariant.option2;
      const baseTitle = this.product.title;
      
      const basePack = this.getBasePack();
      if (pack === basePack) {
        titleEl.textContent = baseTitle;
      } else {
        titleEl.textContent = `${baseTitle} ${pack}`;
      }
    }
    
    if (subtitleEl) {
      const color = this.currentVariant.option1;
      if (color === 'Karışık') {
        const solidColors = [...new Set(this.variants.map(v => v.option1))]
          .filter(c => c !== 'Karışık' && !c.includes(' + '));
        subtitleEl.textContent = solidColors.join(', ');
      } else if (color.includes(' + ')) {
        subtitleEl.textContent = color.replace(/ \+ /g, ', ');
      } else {
        subtitleEl.textContent = color;
      }
    }
  }
  
  updateColorSwatches() {
    const currentColor = this.currentVariant.option1;
    
    document.querySelectorAll('.color-swatch').forEach(swatch => {
      const color = swatch.dataset.colorValue;
      const isActive = color === currentColor;
      
      // Check if ANY variant of this color is available (not just current pack)
      const anyAvailable = this.isColorAvailable(color);
      
      swatch.classList.toggle('active', isActive);
      swatch.classList.toggle('unavailable', !anyAvailable);
      swatch.disabled = !anyAvailable;
    });
    
    // Update color description
    const descEl = document.getElementById('color-description');
    if (descEl) {
      const description = this.getColorDescription(currentColor);
      descEl.innerHTML = `<strong>${currentColor}</strong> <span>${description}</span>`;
    }
  }
  
  getColorDescription(color) {
    if (color.includes(' + ')) return color.split(' + ').join(' ve ');

    const descriptions = {
      'Gonca Pembe': 'Toz pembe ile şeftali arasında bir pudra mercan',
      'İnci Beyaz': 'Taş beyazı ve fildişi krem arasında hareketli tonlarda',
      'Inci Beyaz': 'Taş beyazı ve fildişi krem arasında hareketli tonlarda',
      'Sayfi Mavi': 'Sisli okyanus mavisi tonlarında',
      'Müdavim Yeşil': 'Adaçayı tonlarında, sisli bir yeşil',
      'Mudavim Yesil': 'Adaçayı tonlarında, sisli bir yeşil',
      'Dem Kırmızı': 'Yanık kiremit ile nar kırmızısı arası tonlarında',
      'Dem Kirmizi': 'Yanık kiremit ile nar kırmızısı arası tonlarında',
      'Firuze Petrol Yeşili': 'bir bakarsın mavi gözükür bir bakarsın yeşil gözükür',
      'Firuze Petrol Yesili': 'bir bakarsın mavi gözükür bir bakarsın yeşil gözükür'
    };
    // If no match in descriptions, it's a mixed color (e.g., Karışık)
    return descriptions[color] || 'Tüm renkler bir arada';
  }
  
  updatePackButtons() {
    const currentColor = this.currentVariant.option1;
    const currentPack = this.currentVariant.option2;

    document.querySelectorAll('.pack-btn').forEach(btn => {
      const pack = btn.dataset.packValue;
      const isActive = pack === currentPack;

      // Check if this specific color+pack combo is available
      let variant = this.findVariant(currentColor, pack);
      // Fallback: find any available variant for this pack (e.g., Karışık sets)
      if (!variant || !variant.available) {
        variant = this.findFirstAvailableVariantForPack(pack);
      }
      const isAvailable = variant && variant.available;

      btn.classList.toggle('active', isActive);
      btn.classList.toggle('unavailable', !isAvailable);
      btn.disabled = !isAvailable;
      
      if (variant) {
        btn.dataset.variantId = variant.id;
        
        const priceEl = btn.querySelector('.pack-btn-price');
        const compareEl = btn.querySelector('.pack-btn-compare');
        
        if (priceEl) {
          priceEl.textContent = this.formatMoney(variant.price);
        }
        
        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = this.formatMoney(variant.compare_at_price);
            compareEl.style.display = '';
          } else {
            compareEl.style.display = 'none';
          }
        }

        let savingsEl = btn.querySelector('.pack-btn-savings');
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const pct = Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100);
          if (!savingsEl) {
            savingsEl = document.createElement('span');
            savingsEl.className = 'pack-btn-savings';
            btn.appendChild(savingsEl);
          }
          savingsEl.textContent = `-%${pct}`;
          savingsEl.style.display = '';
        } else if (savingsEl) {
          savingsEl.style.display = 'none';
        }
      }
    });
  }
  
  updateGallery() {
    const currentColor = this.currentVariant.option1;
    const currentPack = this.currentVariant.option2;

    let filteredImages = this.imagesData.images.filter(img => {
      if (!img.color) return false;
      if (img.color !== currentColor) return false;

      if (img.pack === currentPack) return true;
      if (img.pack === null) return true;

      return false;
    });

    // Fallback: if no images match color+pack, show all images for this color
    if (filteredImages.length === 0) {
      filteredImages = this.imagesData.images.filter(img => img.color === currentColor);
    }

    // Fallback for "Karışık": show shared images or all images
    if (filteredImages.length === 0 && currentColor === 'Karışık') {
      filteredImages = this.imagesData.images.filter(img => img.color && img.pack === null);
      if (filteredImages.length === 0) {
        filteredImages = this.imagesData.images.filter(img => img.color != null);
      }
    }

    const mainContainer = document.getElementById('gallery-main-image');
    const thumbContainer = document.getElementById('gallery-thumbnails');
    
    if (mainContainer && filteredImages.length > 0) {
      mainContainer.innerHTML = filteredImages.map((img, index) => `
        <img 
          src="${img.src}"
          data-src="${img.srcLarge}"
          data-zoom="${img.srcZoom}"
          alt="${img.alt || ''}"
          class="gallery-main-img${index === 0 ? ' active' : ''}"
          data-image-id="${img.id}"
          data-index="${index}"
          width="800"
          height="800"
          loading="${index === 0 ? 'eager' : 'lazy'}"
        >
      `).join('');
    }
    
    if (thumbContainer && filteredImages.length > 0) {
      thumbContainer.innerHTML = filteredImages.map((img, index) => `
        <button 
          class="gallery-thumbnail${index === 0 ? ' active' : ''}"
          data-image-id="${img.id}"
          data-index="${index}"
          aria-label="View image ${index + 1}"
        >
          <img src="${img.thumb}" alt="${img.alt || ''}" width="85" height="85" loading="lazy">
        </button>
      `).join('');
      
      thumbContainer.querySelectorAll('.gallery-thumbnail').forEach(thumb => {
        thumb.addEventListener('click', (e) => {
          const index = parseInt(e.currentTarget.dataset.index);
          this.selectImageByIndex(index);
        });
      });
    }
    
    this.currentImageIndex = 0;
  }
  
  selectImage(imageId) {
    const mainImages = document.querySelectorAll('.gallery-main-img');
    const thumbnails = document.querySelectorAll('.gallery-thumbnail');
    
    mainImages.forEach(img => {
      img.classList.toggle('active', img.dataset.imageId == imageId);
    });
    
    thumbnails.forEach(thumb => {
      thumb.classList.toggle('active', thumb.dataset.imageId == imageId);
    });
  }
  
  selectImageByIndex(index) {
    const mainImages = document.querySelectorAll('.gallery-main-img');
    const thumbnails = document.querySelectorAll('.gallery-thumbnail');
    
    mainImages.forEach((img, i) => {
      img.classList.toggle('active', i === index);
    });
    
    thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle('active', i === index);
    });
    
    this.currentImageIndex = index;
  }
  
  navigateGallery(direction) {
    const mainImages = document.querySelectorAll('.gallery-main-img');
    const totalImages = mainImages.length;
    
    if (totalImages === 0) return;
    
    let newIndex = (this.currentImageIndex || 0) + direction;
    
    if (newIndex < 0) newIndex = totalImages - 1;
    if (newIndex >= totalImages) newIndex = 0;
    
    this.selectImageByIndex(newIndex);
  }
  
  updatePrice() {
    const priceEl = document.querySelector('[data-product-price]');
    const comparePriceEl = document.querySelector('[data-compare-price]');
    const discountBadgeEl = document.querySelector('[data-discount-badge]');
    const discountPctEl = document.querySelector('[data-discount-pct]');
    const hasDiscount = this.currentVariant.compare_at_price > this.currentVariant.price;

    if (priceEl) {
      priceEl.textContent = this.formatMoney(this.currentVariant.price);
    }

    if (comparePriceEl) {
      if (hasDiscount) {
        comparePriceEl.textContent = this.formatMoney(this.currentVariant.compare_at_price);
        comparePriceEl.style.display = '';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }

    if (discountBadgeEl) {
      if (hasDiscount) {
        const pct = Math.floor((this.currentVariant.compare_at_price - this.currentVariant.price) * 100 / this.currentVariant.compare_at_price);
        if (discountPctEl) discountPctEl.textContent = pct;
        discountBadgeEl.style.display = '';
      } else {
        discountBadgeEl.style.display = 'none';
      }
    }
  }
  
  updateAddToCart() {
    const addToCartBtn = document.querySelector('[data-add-to-cart]');
    const variantInput = document.querySelector('input[name="id"]');
    
    if (variantInput) {
      variantInput.value = this.currentVariant.id;
    }
    
    if (addToCartBtn) {
      addToCartBtn.disabled = !this.currentVariant.available;
      
      const btnText = addToCartBtn.querySelector('[data-add-to-cart-text]');
      if (btnText) {
        btnText.textContent = this.currentVariant.available ? '+ SEPETE EKLE' : 'TÜKENDİ';
      }
    }
  }
  
  updateURL() {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url.toString());
  }
  
  formatMoney(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('₺', '₺');
  }
}

document.addEventListener('DOMContentLoaded', function() {
  const productDataEl = document.getElementById('product-json-data');
  const variantsDataEl = document.getElementById('product-variants-data');
  
  if (productDataEl && variantsDataEl) {
    try {
      const productData = JSON.parse(productDataEl.textContent);
      const variantsData = JSON.parse(variantsDataEl.textContent);
      
      window.productVariantController = new ProductVariantController(productData, variantsData);
    } catch (e) {
      console.error('Failed to initialize ProductVariantController:', e);
    }
  }
});